// src/lib/facture-html.js — Générateur HTML de la facture mensuelle proprio.
//
// Extrait de src/App.jsx::genFactureHTML pour permettre :
// - L'utilisation par un script Node (scripts/migrate-factlog-snapshot.js)
//   qui ne peut pas importer App.jsx (state React closure).
// - L'usage de cette fonction comme cache/snapshot stocké dans factLog
//   (cf. PR feat/historique-actions, ADR à venir).
//
// Refacto PURE : tous les inputs sont passés en paramètres, aucune
// fermeture sur le state React. Le HTML produit est strictement
// identique à l'ancienne version inline (zéro changement de comportement).
//
// Pour la facture par résa (1 voyageur = 1 facture, Phase 2.E.11),
// voir src/lib/facture-resa-html.js — système distinct.

import { calc as calcPure } from './calc'
import { resoudreCommission } from './commission-taux-date'
import { lignesCommissionFacture, commissionTotaleFacture } from './commission-lignes'
import { LOGO_B64 } from '../assets/logo'
import { getDegs, isFactureEtat } from './incidents-helpers'
import { numeroTvaImprimable } from './emitter-resolver'
import { echapperHtml as esc } from './echapper-html'

// Échappement du numéro de TVA SAISI (texte libre de la conciergerie) : jamais
// interprété comme du HTML sur une pièce comptable.
const escTva = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

// Faille N1 (revue du 29/10, lot L5) : TOUTE valeur venue des données
// (voyageur, logement, propriétaire, réglages, libellés, numéro, dates) passe
// par `esc` (src/lib/echapper-html.js) avant d'entrer dans le document. Une
// facture sans caractère spécial ne change pas d'un octet.
// Garde : src/lib/facture-html.echappement.test.js.

// Placeholder utilisé dans le HTML produit pour économiser ~25 kB par
// snapshot stocké dans factLog (cf. ADR à venir). Le HTML produit par
// genFactureHTML contient `<img src="__LOGO_B64__" .../>` au lieu du
// data:image/png;base64,... réel. Avant tout rendu visuel (email,
// window.open), passer le HTML par inflateFactureSnapshot() pour
// remplacer le placeholder par la vraie data URI.
const LOGO_PLACEHOLDER = '__LOGO_B64__'

/**
 * Remplace le placeholder logo par la vraie data URI base64 PNG.
 * À appeler AVANT window.open / envoi email sur tout HTML produit par
 * genFactureHTML ou stocké dans factLog[i].htmlSnapshot.
 *
 * @param {string} html - HTML avec placeholder __LOGO_B64__
 * @returns {string} HTML prêt pour rendu visuel (logo intégré)
 */
export function inflateFactureSnapshot(html) {
  if (typeof html !== 'string' || !html) return html
  return html.split(LOGO_PLACEHOLDER).join(`data:image/png;base64,${LOGO_B64}`)
}

// ── Chantier « numéro attribué À L'ENVOI » ───────────────────────────────
//
// Un BROUILLON est figé SANS numéro : le HTML stocké dans invoices.html_snapshot
// porte ce jeton à la place du numéro. La RPC assign_invoice_number() le
// substitue par le numéro réel DANS LA MÊME TRANSACTION que l'incrément du
// compteur (cf. migration 20261115000001) — HTML et numéro ne peuvent donc pas
// diverger, et aucun numéro n'est consommé tant que rien n'est envoyé.
//
// ⚠️ La valeur DOIT rester strictement identique à celle codée en dur dans la
// migration (`replace(html_snapshot, '__INVOICE_NUMBER__', v_number)`) et dans
// la garde fail-closed `position('__INVOICE_NUMBER__' IN ...)`.
export const INVOICE_NUMBER_PLACEHOLDER = '__INVOICE_NUMBER__'

// Jeton DATE D'ÉMISSION — décision propriétaire 2026-08-05 (option A) :
// « Une facture est émise le jour où elle reçoit son numéro, pas le jour où je
// l'ai imprimée. » Sans ce jeton, un brouillon imprimé le 03/09 et envoyé en
// octobre porterait un numéro d'octobre sur une date de septembre → la série
// cesserait d'être croissante en date, ce qui casse la chronologie exigée par
// l'article 289 du CGI.
//
// ⚠️ La substitution SQL produit `to_char(..., 'DD/MM/YYYY')`, STRICTEMENT le
// même format que `toLocaleDateString('fr-FR')` utilisé partout ailleurs
// (« 03/06/2026 ») : le document ne change pas d'apparence.
export const INVOICE_DATE_PLACEHOLDER = '__INVOICE_DATE__'

// Libellé affiché à l'écran / à l'impression pour un brouillon. Volontairement
// NON numérique : jamais de faux numéro qui ressemblerait à un vrai (exigence
// Dimitri — un brouillon doit se voir).
export const DRAFT_NUMBER_LABEL = 'BROUILLON'

// Un brouillon n'a PAS de date d'émission (il n'est pas émis). On n'affiche donc
// NI la date du jour — qui serait un faux — NI un vide muet.
export const DRAFT_DATE_LABEL = '— (non émise)'

/**
 * Rend un snapshot de BROUILLON lisible à l'écran : remplace les jetons
 * __INVOICE_NUMBER__ et __INVOICE_DATE__ par des libellés explicites.
 *
 * À appeler pour tout affichage/impression d'une pièce NON encore envoyée.
 * NE JAMAIS appliquer avant l'INSERT : c'est le HTML AVEC jetons qui doit être
 * persisté (sinon assign_invoice_number() refuse, garde
 * `html_snapshot_placeholder_missing`).
 *
 * IDEMPOTENT et SANS EFFET sur un document déjà émis (les 29 factures
 * historiques ne portent aucun jeton) : les deux `split/join` sont des no-op
 * quand le jeton est absent. Peut donc être appliqué sans condition.
 *
 * @param {string} html - HTML portant les jetons
 * @returns {string}
 */
export function renderDraftPlaceholders(html) {
  if (typeof html !== 'string' || !html) return html
  return html
    .split(INVOICE_NUMBER_PLACEHOLDER).join(DRAFT_NUMBER_LABEL)
    .split(INVOICE_DATE_PLACEHOLDER).join(DRAFT_DATE_LABEL)
}

/**
 * Les MONTANTS de la facture mensuelle proprio, sans le HTML.
 *
 * Source UNIQUE du « Total à régler » : genFactureHTML l'imprime, l'écran
 * Facturation l'affiche sous « À facturer » (décision Dimitri 2026-10-01 : le
 * montant affiché à l'écran = le montant facturé, prestations, remplacements
 * et ajustements compris — avant, l'écran disait commission + ménage : l10
 * septembre 2026 113,14 € à l'écran contre 340,37 € sur la facture).
 *
 * Déplacement À L'IDENTIQUE depuis genFactureHTML : mêmes lectures, même
 * ordre, mêmes arrondis. Aucun refus ici (fonction d'affichage aussi) : les
 * refus de génération restent dans genFactureHTML ; un montant incalculable
 * sort en NaN, jamais en 0.
 *
 * Mêmes arguments que genFactureHTML (log, cfg, data, incidents, month, y, m).
 */
export function totauxFacture({ log, cfg, data, incidents, month, y, m }) {
  // `calcPure` reçoit `month` : il résout lui-même le barème daté et, dans ce
  // cas, ignore l'argument `log.taux` qu'on lui passe encore (inchangé).
  const c = calcPure(cfg, data, log.id, log.taux, month)
  // ── Taux DATÉ (lot taux-date) ──
  // Le barème EN VIGUEUR au mois facturé. Sans `tauxHistorique` sur le
  // logement, `historise` est false et `bareme` porte les champs plats du
  // logement : toutes les lectures ci-dessous restent celles d'avant.
  // ⚠️ `month` est la clé « AAAA-MM », jamais le libellé `monthLabel`.
  const baremeFacture = resoudreCommission(log, month)
  const baremeSrc = baremeFacture.historise ? baremeFacture : log
  const resas = data?.[month]?.[log.id]?.reservations || []
  const extras = data?.[month]?.[log.id]?.extras || []
  const ajustements = data?.[month]?.[log.id]?.ajustements || []

  // Remplacements refacturés au propriétaire, facturables ce mois (remplaceLe
  // dans le mois en cours). Chantier ⑥ — 5 états financiers : la ligne facture
  // est alimentée par les états « Refacturé propriétaire » ET « AirCover +
  // refacturé » (isFactureEtat), au BRUT. JAMAIS de déduction AirCover ici :
  // l'AirCover est perçu par le proprio (hors facture) — décision Dimitri, sinon
  // double remboursement. Rétrocompat : l'ancien `traitement:'fbz'` mappe vers
  // « Refacturé propriétaire » (deriveEtatFinancier) → facture byte-identique.
  const remplacements = []
  ;(incidents || []).forEach(inc => {
    if (inc.logId !== log.id) return
    getDegs(inc).forEach(d => {
      if (!isFactureEtat(d) || !d.remplaceLe) return
      const rd = new Date(d.remplaceLe)
      if (rd.getFullYear() === y && rd.getMonth() + 1 === m) {
        remplacements.push({
          label: `${Number(d.nombre) > 1 ? d.nombre + '× ' : ''}${d.type}${d.precision ? ' (' + d.precision + ')' : ''}`,
          voyageur: inc.voyageur || '—',
          date: new Date(d.remplaceLe).toLocaleDateString('fr-FR'),
          cout: parseFloat(d.cout) || 0, // BRUT — jamais − AirCover
        })
      }
    })
  })
  const totalRemplacements = remplacements.reduce((s, r) => s + r.cout, 0)
  const totalAjustements = ajustements.reduce((s, a) => s + (a.montant || 0), 0)
  const totalExtras = extras.reduce((s, e) => s + (e.montant || 0), 0)

  // ═══════════════════════════════════════════════════════════════════════
  // 🔴 LES LIGNES NE S'ADDITIONNAIENT PAS — corrigé le 2026-08-05
  // ═══════════════════════════════════════════════════════════════════════
  // Chaque ligne imprimait sa commission ARRONDIE au centime (`toFixed(2)`)
  // tandis que le TOTAL imprimait `c.comm`, somme des commissions NON
  // arrondies : Σ(arrondis) ≠ arrondi(Σ). Un propriétaire qui additionnait la
  // colonne « Comm. » à la calculatrice ne retombait pas sur le total.
  //
  // Constat sur les 22 factures P déjà envoyées (base de PRODUCTION, lue le
  // 2026-08-05) : 11 sont incohérentes, de 1 à 2 centimes — P-2026-0029,
  // 0032, 0033, 0034, 0036, 0037, 0040, 0042, 0043, 0044, 0047.
  //
  // CORRECTIF : la commission de CHAQUE ligne est arrondie au centime UNE
  // FOIS, ici, et TOUS les totaux du document découlent de ces mêmes valeurs
  // arrondies. Le document redevient donc arithmétiquement vérifiable : la
  // colonne « Comm. » et la colonne « Total FBZ » s'additionnent exactement.
  //
  // Une commission est une somme d'argent réellement due par réservation :
  // elle doit être exprimable en centimes. Arrondir par ligne PUIS sommer est
  // donc aussi le calcul le plus juste, pas seulement le plus lisible.
  //
  // ⚠️ Les factures DÉJÀ ÉMISES ne sont PAS retouchées (elles sont gelées par
  // le verrou d'immuabilité) : ce correctif ne vaut que pour les suivantes.

  // Arrondi 2 décimales. NE MASQUE PLUS les NaN (paquet argent 2026-09-03) :
  // l'ancien `Number(n)||0` transformait un calcul raté en 0,00 € imprimé.
  // Un non-fini RESTE NaN et déclenche le REFUS de génération plus bas.
  // null/undefined explicites restent 0 (les appels passent des valeurs déjà
  // défaultées `|| 0`).
  const round2 = n => {
    const v = Number(n ?? 0)
    return Number.isFinite(v) ? Math.round(v * 100) / 100 : NaN
  }
  // Lignes + total commission : source UNIQUE partagée avec l'écran
  // Facturation (src/lib/commission-lignes.js, défaut du 2026-09-30 : l'écran
  // appliquait le taux au total des bases, la facture arrondit par ligne).
  // Déplacement à l'identique : même tri, même taux, même arrondi par ligne.
  const lignesResas = lignesCommissionFacture(resas, { baremeSrc, tauxFixe: baremeSrc.taux })
  // Totaux du tableau — dérivés des lignes IMPRIMÉES, jamais recalculés à part.
  const commTotal = commissionTotaleFacture(lignesResas)
  const totalFbzTotal = round2(lignesResas.reduce((s, l) => s + l.totalFbz, 0))

  const montantTotal = round2(commTotal + c.men + totalExtras + totalRemplacements + totalAjustements)

  return {
    c, baremeSrc, resas, extras, ajustements, remplacements,
    totalRemplacements, totalAjustements, totalExtras,
    lignesResas, commTotal, totalFbzTotal, montantTotal,
  }
}

/**
 * « Net propriétaire » de l'écran Facturation = CA brut − total EXACT de la
 * facture (revue #1559) : le `montantTotal` de totauxFacture, la somme de
 * lignes arrondies que le PDF imprime — jamais une seconde formule. Avant :
 * c.net − prestations − remplacements − ajustements, qui ne passait pas par
 * l'arrondi du total (un ajustement à 3 décimales décalait d'un centime).
 *
 * La part des résas directes FBZ, que calc() ajoute au CA et que la facture ne
 * porte pas, garde son net : c.net (fusionné) − t.c.net (celui de la facture).
 *
 * @param {object} t - totauxFacture(...)
 * @param {object} c - calc() de l'écran (KV + résas directes FBZ)
 * @returns {number} au centime ; NaN si un montant est incalculable (jamais 0)
 */
export function netProprietaireFacture(t, c) {
  const partDirecte = c.net - t.c.net
  const v = t.c.ca - t.montantTotal + partDirecte
  return Number.isFinite(v) ? Math.round(v * 100) / 100 : NaN
}

/**
 * Génère le HTML de la facture mensuelle proprio.
 *
 * @param {Object}   args
 * @param {Object}   args.prop        - cfg.props[i] (proprio)
 * @param {Object}   args.log         - cfg.props[i].logs[j] (logement)
 * @param {Object}   args.cfg         - blob fbz8-cfg complet
 * @param {Object}   args.data        - blob fbz8-data complet
 * @param {Array}    args.incidents   - incidents en shape KV-compat (logId/
 *   degradations/voyageur). LOT Incidents 13 : la SOURCE est la table V2
 *   `incidents` (mappée via mapV2IncidentsToKv côté App.jsx) — plus le blob KV
 *   fbz8-incidents. Le rendu (filtre inc.logId / getDegs / remplaceLe / coûts)
 *   est INCHANGÉ : seule la provenance des incidents change.
 * @param {string}   args.month       - clé mois "YYYY-MM"
 * @param {string}   args.monthLabel  - libellé mois "Avril 2026"
 * @param {number}   args.y           - année numérique (ex: 2026)
 * @param {number}   args.m           - mois numérique 1-12 (ex: 4 pour avril)
 * @param {string}   [args.mode='email'] - 'email' ou 'preview' (preview = bouton flottant Imprimer)
 * @param {string}   [args.invoice_number=null] - numéro RÉEL consommé via RPC (émission). Prime sur previewNumber.
 * @param {string}   [args.previewNumber=null] - numéro PRÉVU lecture seule pour l'aperçu AVANT émission
 *   (suffixé « (aperçu) » en mode preview). JAMAIS consommé en DB, JAMAIS passé à createInvoice.
 * @param {boolean}  [args.draft=false] - mode BROUILLON : le HTML est figé avec les jetons
 *   `__INVOICE_NUMBER__` (à la place du numéro) ET `__INVOICE_DATE__` (à la place de la
 *   date d'émission). C'est ce HTML-là qui est persisté dans invoices.html_snapshot ;
 *   la RPC assign_invoice_number() substituera numéro ET date à l'envoi, dans la même
 *   transaction (option A — la pièce est émise le jour où elle reçoit son numéro).
 *   Pour l'affichage/impression du brouillon, passer le résultat par
 *   renderDraftPlaceholders(). EXCLUSIF avec invoice_number et previewNumber.
 * @returns {{html: string, nextNum: number, montantTotal: number}}
 */
export function genFactureHTML({ prop, log, cfg, data, incidents, month, monthLabel, y, m, mode = 'email', invoice_number = null, previewNumber = null, draft = false }) {
  // Montants : source unique partagée avec l'écran (totauxFacture ci-dessus).
  const {
    c, baremeSrc, resas, extras, ajustements, remplacements,
    totalRemplacements, totalAjustements, totalExtras,
    lignesResas, commTotal, totalFbzTotal, montantTotal,
  } = totauxFacture({ log, cfg, data, incidents, month, y, m })
  // Phase 3 PR 6 (ADR-066) + Pass B : à l'ÉMISSION, le numéro provient TOUJOURS
  // du RPC séquentiel atomique (invoice_sequences). Le fallback mort cfg.lastNum a
  // été retiré (Pass B) : la numérotation n'est plus jamais dérivée du KV. Tout
  // appelant ÉMETTEUR DOIT fournir invoice_number (les 2 call-sites App.jsx le font
  // via nextInvoiceNumberV2). ⚠️ CHEMIN ARGENT + CGI 289.
  //
  // BUG 3 (aperçu commission) — calque du pattern aperçu voyageur (facture-resa-html.js) :
  // en APERÇU avant émission, l'appelant passe `previewNumber` (numéro PRÉVU
  // lecture seule, calculé via computePreviewNumber) SANS invoice_number. La garde
  // reste NON affaiblie pour l'émission : throw si NI invoice_number NI previewNumber.
  // previewNumber NE consomme RIEN en DB, n'atteint JAMAIS createInvoice.
  //
  // MODE BROUILLON (chantier « numéro à l'envoi ») — 3e entrée admise : `draft`.
  // Le document est figé SANS numéro, avec le jeton `__INVOICE_NUMBER__`. Aucun
  // numéro n'est consommé → la pièce reste supprimable/corrigeable sans trouer
  // la série. EXCLUSIF avec les deux autres entrées : passer `draft` ET un
  // numéro (réel ou prévu) est une erreur d'appelant, pas un cas à arbitrer.
  if (draft && (invoice_number || previewNumber)) {
    throw new Error('facture-html: draft est exclusif de invoice_number/previewNumber')
  }
  if (!invoice_number && !previewNumber && !draft) throw new Error('facture-html: invoice_number requis (numérotation par RPC)')
  // Mention « (aperçu) » conditionnée au mode preview, posée ICI (pas dans le
  // helper de numéro). En émission (invoice_number fourni) : jamais de mention.
  const apercu = mode === 'preview' ? ' (aperçu)' : ''
  const numFact = draft ? INVOICE_NUMBER_PLACEHOLDER : (invoice_number || `${previewNumber}${apercu}`)
  // Rétrocompat de la forme de retour { nextNum } : dérivé des 4 derniers
  // chiffres du numéro affiché (jamais consommé par les appelants actuels).
  // Sur numFact (string) → tolère le suffixe « (aperçu) » sans crash (NaN→0).
  const nextNum = parseInt(String(numFact).slice(-4), 10) || 0
  // BROUILLON : la date d'émission n'est PAS connue à la création — elle sera
  // posée par assign_invoice_number(), en même temps que le numéro et dans la
  // même transaction (décision propriétaire, option A). On fige donc le jeton.
  // Émission directe / aperçu : comportement d'origine INCHANGÉ.
  const dateEmission = draft ? INVOICE_DATE_PLACEHOLDER : new Date().toLocaleDateString('fr-FR')


  // ═══════════════════════════════════════════════════════════════════════
  // 🔴 NUMÉRO DE TVA — jamais écrit en dur (décisions du 28/10, n° 25 et 34 ;
  //    consigne Dimitri du 29/10)
  // ═══════════════════════════════════════════════════════════════════════
  // Le gabarit imprimait, si la case « Assujetti TVA » était cochée, un numéro
  // de REPLI codé en dur (FR65927602805) : 29 factures émises le portent (note
  // au dossier, pas de réémission — elles restent figées).
  // Désormais : le numéro SAISI par la conciergerie, seulement si elle est
  // assujettie (règle unique : numeroTvaImprimable, emitter-resolver.js),
  // échappé ; sinon la seule mention de franchise 293 B.
  // Garde : src/lib/tva-jamais-imprimee.test.js.
  const numeroTva = numeroTvaImprimable(cfg.co)


  // ═══════════════════════════════════════════════════════════════════════
  // 🔴 REFUS DE GÉNÉRATION — le zéro qui ment / la commission muette
  // (paquet argent 2026-09-03). Une facture ne doit JAMAIS porter un montant
  // faux : `round2` (Number(n)||0) maquillait tout NaN en 0,00 € imprimé, et
  // un taux manquant imprimait des lignes à commission 0 (total amputé).
  // Règles :
  //   - taux de commission incalculable (mode non-dégressif, base > 0) → REFUS ;
  //   - un montant fourni mais non-fini (NaN/Infinity) sur une résa, un extra
  //     ou un ajustement → REFUS.
  // Un montant ABSENT (null/undefined : « résa sans montant », badgée à
  // l'écran) et un VRAI 0 restent traités comme aujourd'hui : ils ne
  // déclenchent PAS le refus. Les appelants (émission, brouillon, aperçu)
  // attrapent l'erreur et affichent le message.
  const montantFourniIncalculable = v => v != null && v !== '' && !Number.isFinite(Number(v))
  if (baremeSrc.commissionMode !== 'degressive'
      && !Number.isFinite(Number(baremeSrc.taux ?? NaN))
      && lignesResas.some(l => l.nuitee > 0)) {
    throw new Error(`commission non calculée sur ${log.nom} : taux manquant — génération de facture refusée`)
  }
  const resaIncalculable = lignesResas.find(({ r }) => montantFourniIncalculable(r.montant) || montantFourniIncalculable(r.menage))
  if (resaIncalculable
      || extras.some(e => e && montantFourniIncalculable(e.montant))
      || ajustements.some(a => a && montantFourniIncalculable(a.montant))
      || !Number.isFinite(montantTotal)) {
    throw new Error(`facture ${log.nom} refusée : montant incalculable — donnée manquante (aucune facture ne doit porter un montant faux)`)
  }

  const showExtras = totalExtras > 0
  const showRemp = totalRemplacements > 0
  const showAjust = Math.abs(totalAjustements) > 0
  const cellCount = 2 + (showExtras ? 1 : 0) + (showRemp ? 1 : 0) + (showAjust ? 1 : 0)

  const plBadge = pl => {
    const colors = { airbnb: '#FF5A5F', booking: '#003580', direct: '#2d7a3a' }
    const labels = { airbnb: 'Airbnb', booking: 'Booking', direct: 'Direct' }
    return `<span style="background:${colors[pl] || '#888'}22;color:${colors[pl] || '#888'};border-radius:4px;padding:2px 8px;font-size:11px;font-weight:700">${esc(labels[pl] || pl)}</span>`
  }

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Facture ${esc(numFact)}</title>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Raleway:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  @page{size:A4;margin:0.5cm}
  body{font-family:'Raleway',sans-serif;background:#eef2f7;color:#333;padding:0}
  .page{background:#fff;max-width:700px;margin:0 auto;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,.1)}
  .print-btn{position:fixed;top:20px;right:20px;background:#215387;color:#fff;border:none;border-radius:8px;padding:10px 18px;font-size:13px;font-weight:700;cursor:pointer;font-family:'Raleway',sans-serif;box-shadow:0 4px 12px rgba(33,83,135,.3);z-index:9999}
  .print-btn:hover{background:#1a3f66}
  .header{background:linear-gradient(135deg,#1a3f66,#215387,#3a72a8);color:#fff;padding:14px 24px;display:flex;justify-content:space-between;align-items:center}
  .logo-area{display:flex;align-items:center;gap:14px}
  .logo{width:52px;height:52px;border-radius:50%;object-fit:cover;border:2px solid rgba(255,255,255,.3)}
  .company h1{font-family:'Playfair Display',serif;font-size:18px;font-weight:700;letter-spacing:.5px}
  .company p{font-size:11px;opacity:.7;margin-top:2px}
  .fact-num{text-align:right}
  .fact-num .num{font-family:'Playfair Display',serif;font-size:16px;font-weight:700}
  .fact-num .date{font-size:11px;opacity:.7;margin-top:4px}
  .body{padding:14px 24px}
  .meta{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:10px}
  .meta-box{background:#f8fafd;border-radius:10px;padding:10px 12px}
  .meta-box h3{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;font-weight:700}
  .meta-box p{font-size:11px;color:#333;line-height:1.4}
  .meta-box strong{font-weight:700;color:#215387}
  .section-title{font-family:'Playfair Display',serif;font-size:12px;color:#215387;font-weight:700;margin-bottom:6px;padding-bottom:4px;border-bottom:2px solid #eef2f7;page-break-after:avoid}
  table{width:100%;border-collapse:collapse;margin-bottom:8px;font-size:11px}
  th{background:#f0f4f8;padding:5px 8px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:.4px;color:#555;font-weight:700}
  th.r,td.r{text-align:right}
  td{padding:5px 8px;border-bottom:1px solid #f0f4f8;color:#444}
  tr{page-break-inside:avoid}
  tr:last-child td{border-bottom:none}
  .end-block{page-break-inside:avoid}
  .total-box{background:#87b8e6;border:2px solid #87b8e6;color:#fff;border-radius:10px;padding:10px 16px;display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}
  .total-box .label{font-size:13px;font-weight:700;color:#fff;text-transform:uppercase;letter-spacing:.5px}
  .total-box .amount{font-family:'Playfair Display',serif;font-size:22px;font-weight:800;color:#fff}
  .rib{background:#f0f4f8;border-radius:10px;padding:10px 12px;margin-bottom:8px}
  .rib h3{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;font-weight:700}
  .rib p{font-size:11px;color:#444;line-height:1.4}
  .footer{text-align:center;font-size:9px;color:#bbb;padding:8px;border-top:1px solid #f0f4f8;page-break-inside:avoid}
  @media print{
    body{padding:0;background:#fff}
    .page{box-shadow:none;border-radius:0;max-width:100%}
    .print-btn{display:none!important}
    *{-webkit-print-color-adjust:exact;color-adjust:exact;print-color-adjust:exact}
  }
</style>
</head>
<body>
${mode === 'preview' ? `<button class="print-btn" onclick="window.print()">🖨️ Imprimer / Enregistrer en PDF</button>` : ''}
<div class="page">
  <div class="header">
    <div class="logo-area">
      <img src="${LOGO_PLACEHOLDER}" class="logo" alt="logo"/>
      <div class="company">
        <h1>FULLBNBZEN</h1>
        <p>SAS au capital de 100&nbsp;€</p>
        <p>${esc(cfg.co?.adresse || '')}</p>
      </div>
    </div>
    <div class="fact-num">
      <div class="num">N° ${esc(numFact)}</div>
      <div class="date">Émise le ${esc(dateEmission)}</div>
      <div class="date" style="margin-top:4px">Période : ${esc(monthLabel)}</div>
    </div>
  </div>
  <div class="body">
    <div class="meta">
      <div class="meta-box">
        <h3>Prestataire</h3>
        <p><strong>FULLBNBZEN SAS</strong><br>
        ${esc(cfg.co?.adresse || '')}<br>
        SIRET : ${esc(cfg.co?.siret || '927 602 805 00016')}<br>
        RCS Toulouse 927 602 805<br>
        ${numeroTva
          ? `N°TVA : ${escTva(numeroTva)}<br>`
          : `<span style="font-size:11px;color:#888;font-style:italic">TVA non applicable, art. 293 B du CGI</span>`}</p>
        <p style="margin-top:6px">${esc(cfg.co?.email || '')}${cfg.co?.tel ? `<br>Tél : ${esc(cfg.co.tel)}` : ''}</p>
      </div>
      <div class="meta-box">
        <h3>Client</h3>
        <p><strong>${esc(prop.nom)}</strong><br>
        Logement : ${esc(log.nom)}<br>
        ${(() => {
          // Mode dégressif : afficher la moyenne pondérée des taux appliqués
          // sur les résas du mois (chaque résa peut avoir un taux différent
          // selon sa durée). Cas edge : pas de résa ou base = 0 → libellé
          // générique pour éviter division par zéro.
          if (baremeSrc.commissionMode === 'degressive') {
            const totalBase = resas.reduce((s, r) => s + Math.max(0, (r.montant || 0) - (r.menage || 0)), 0)
            if (totalBase <= 0) return 'Commission dégressive (selon durée)'
            // commTotal (= Σ des lignes imprimées) et non c.comm : le taux moyen
            // affiché doit correspondre à la commission réellement facturée.
            const avgTaux = (commTotal / totalBase) * 100
            return `Commission moyenne : ${avgTaux.toFixed(1).replace('.', ',')}%`
          }
          return `Commission : ${esc(baremeSrc.taux)}%`
        })()}</p>
      </div>
    </div>

    <div class="section-title">Réservations du mois</div>
    <table>
      <thead>
        <tr>
          <th>Voyageur</th>
          <th>Plateforme</th>
          <th class="r">Nuits</th>
          <th class="r">Montant reçu</th>
          <th class="r">Ménage</th>
          ${resas.some(r => r.plateforme === 'direct' && r.taxe > 0) ? '<th class="r">Taxe séjour</th>' : ''}
          <th class="r">Base comm.</th>
          <th class="r">Comm.</th>
          <th class="r">Total FBZ</th>
        </tr>
      </thead>
      <tbody>
        ${lignesResas.map(({ r, nuitee, comm, totalFbz }) => {
          return `<tr>
            <td>${esc(r.voyageur || '—')}</td>
            <td>${plBadge(r.plateforme)}</td>
            <td class="r">${esc(r.nuits || 0)}</td>
            <td class="r">${(r.montant || 0).toFixed(2)}&nbsp;€</td>
            <td class="r">${(r.menage || 0).toFixed(2)}&nbsp;€</td>
            ${resas.some(r => r.plateforme === 'direct' && r.taxe > 0) ? `<td class="r">${r.plateforme === 'direct' ? (r.taxe || 0).toFixed(2) + '&nbsp;€' : '—'}</td>` : ''}
            <td class="r">${nuitee.toFixed(2)}&nbsp;€</td>
            <td class="r" style="font-weight:700;color:#215387">${comm.toFixed(2)}&nbsp;€</td>
            <td class="r" style="font-weight:800;color:#1a3f66">${totalFbz.toFixed(2)}&nbsp;€</td>
          </tr>`
        }).join('')}
        <tr style="background:#f0f4f9;font-weight:700;border-top:2px solid #dde3ec">
          <td colspan="2" style="font-weight:700;color:#555">TOTAL</td>
          <td class="r" style="color:#215387">${esc(resas.reduce((s, r) => s + (r.nuits || 0), 0))}</td>
          <td class="r" style="color:#215387">${resas.reduce((s, r) => s + (r.montant || 0), 0).toFixed(2)}&nbsp;€</td>
          <td class="r" style="color:#888">${resas.reduce((s, r) => s + (r.menage || 0), 0).toFixed(2)}&nbsp;€</td>
          ${resas.some(r => r.plateforme === 'direct' && r.taxe > 0) ? `<td class="r" style="color:#888">${resas.filter(r => r.plateforme === 'direct').reduce((s, r) => s + (r.taxe || 0), 0).toFixed(2)}&nbsp;€</td>` : ''}
          <td class="r" style="color:#215387">${resas.reduce((s, r) => s + Math.max(0, (r.montant || 0) - (r.menage || 0)), 0).toFixed(2)}&nbsp;€</td>
          <td class="r" style="color:#215387">${commTotal.toFixed(2)}&nbsp;€</td>
          <td class="r" style="color:#1a3f66;font-weight:800">${totalFbzTotal.toFixed(2)}&nbsp;€</td>
        </tr>
      </tbody>
    </table>

    ${extras.length > 0 ? `
    <div class="section-title">Prestations supplémentaires</div>
    <table>
      <thead><tr><th>Prestation</th><th class="r">Montant</th></tr></thead>
      <tbody>
        ${extras.map(e => `<tr><td>${esc(e.label)}</td><td class="r" style="font-weight:700">${(e.montant || 0).toFixed(2)}&nbsp;€</td></tr>`).join('')}
      </tbody>
    </table>` : ''}

    ${remplacements.length > 0 ? `
    <div class="section-title" style="color:#c0392b;border-bottom-color:#fce8e8">Remboursements remplacements linge &amp; matériel</div>
    <table>
      <thead><tr><th>Item remplacé</th><th>Voyageur concerné</th><th>Remplacé le</th><th class="r">Coût</th></tr></thead>
      <tbody>
        ${remplacements.map(r => `<tr>
          <td style="font-weight:700;color:#c0392b">${esc(r.label)}</td>
          <td style="color:#555">${esc(r.voyageur)}</td>
          <td style="color:#2d7a3a">${esc(r.date)}</td>
          <td class="r" style="font-weight:700">${r.cout > 0 ? r.cout.toFixed(2) + '&nbsp;€' : '—'}</td>
        </tr>`).join('')}
        <tr style="background:#fdf2f2">
          <td colspan="3" style="font-weight:700;color:#c0392b">Total remboursements</td>
          <td class="r" style="font-weight:700;color:#c0392b">${totalRemplacements.toFixed(2)}&nbsp;€</td>
        </tr>
      </tbody>
    </table>` : ''}

    ${ajustements.length > 0 ? `
    <div class="section-title" style="color:#d97706;border-bottom-color:#fff3cd">Ajustements</div>
    <table>
      <thead><tr><th>Motif</th><th class="r">Montant</th></tr></thead>
      <tbody>
        ${ajustements.map(a => `<tr><td>${esc(a.label)}</td><td class="r" style="font-weight:700;color:${a.montant < 0 ? '#c0392b' : '#2d7a3a'}">${a.montant > 0 ? '+' : ''}${(a.montant || 0).toFixed(2)}&nbsp;€</td></tr>`).join('')}
        <tr style="background:#fffdf0">
          <td style="font-weight:700;color:#d97706">Total ajustements</td>
          <td class="r" style="font-weight:700;color:${totalAjustements < 0 ? '#c0392b' : '#2d7a3a'}">${totalAjustements > 0 ? '+' : ''}${totalAjustements.toFixed(2)}&nbsp;€</td>
        </tr>
      </tbody>
    </table>` : ''}

    <div class="end-block">
    <div style="background:#f8fafd;border-radius:10px;padding:10px;margin-bottom:10px;display:grid;grid-template-columns:${'1fr '.repeat(cellCount).trim()};gap:10px;font-size:11px">
      <div><div style="color:#888;font-size:10px;text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px">Commission</div><div style="font-weight:700;color:#215387;font-size:14px">${commTotal.toFixed(2)}&nbsp;€</div></div>
      <div><div style="color:#888;font-size:10px;text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px">Ménage</div><div style="font-weight:700;font-size:14px">${c.men.toFixed(2)}&nbsp;€</div></div>
      ${showExtras ? `<div><div style="color:#888;font-size:10px;text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px">Extras</div><div style="font-weight:700;font-size:14px">${totalExtras.toFixed(2)}&nbsp;€</div></div>` : ''}
      ${showRemp ? `<div><div style="color:#c0392b;font-size:10px;text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px">Remplacements</div><div style="font-weight:700;color:#c0392b;font-size:14px">${totalRemplacements.toFixed(2)}&nbsp;€</div></div>` : ''}
      ${showAjust ? `<div><div style="color:#d97706;font-size:10px;text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px">Ajustements</div><div style="font-weight:700;color:${totalAjustements < 0 ? '#c0392b' : '#2d7a3a'};font-size:14px">${totalAjustements > 0 ? '+' : ''}${totalAjustements.toFixed(2)}&nbsp;€</div></div>` : ''}
    </div>

    <div class="total-box">
      <div class="label">Total à régler</div>
      <div class="amount">${montantTotal.toFixed(2)}&nbsp;€</div>
    </div>

    <div class="rib">
      <h3>Modalités de règlement</h3>
      <p><strong>Mode :</strong> Virement bancaire</p>
      ${(() => {
        // Échéance = 15 du mois SUIVANT le mois facturé. Format "À régler avant le 15.MM.AAAA". Gestion passage d'année.
        let dueMonth = m + 1, dueYear = y
        if (dueMonth > 12) { dueMonth = 1; dueYear += 1 }
        const dueStr = `15.${String(dueMonth).padStart(2, '0')}.${dueYear}`
        return `<p style="margin-top:4px"><strong>Échéance :</strong> À régler avant le ${dueStr}</p>`
      })()}
      ${(() => {
        // 🔴 COORDONNÉES BANCAIRES — corrigé le 2026-08-05.
        //
        // On lisait `cfg.iban` / `cfg.bic`, À LA RACINE du blob. Or l'écran
        // Paramètres → Général écrit sous `cfg.co.iban` / `cfg.co.bic`
        // (ParamsPanel.jsx). PERSONNE n'a jamais écrit à la racine : toutes les
        // factures propriétaire imprimaient donc l'IBAN codé en dur ci-dessous,
        // et n'imprimaient JAMAIS le BIC — quoi que Dimitri saisisse.
        //
        // Impact au moment du constat : NUL, les deux valeurs étaient
        // identiques. Mais un changement de banque ne serait jamais parti sur
        // les factures. Le repli en dur est CONSERVÉ (une facture sans IBAN
        // n'est pas payable), et il est désormais le dernier recours, pas le
        // cas nominal.
        const iban = cfg.co?.iban || cfg.iban || 'FR76 1741 8000 0100 0118 0200 007'
        const bic = cfg.co?.bic || cfg.bic || ''
        return `<p style="margin-top:4px"><strong>IBAN :</strong> ${esc(iban)}${bic ? ` · <strong>BIC :</strong> ${esc(bic)}` : ''}</p>`
      })()}
      <p style="margin-top:8px;font-size:10px;color:#888;font-style:italic;line-height:1.4">Tout retard de paiement entraînera une indemnité forfaitaire de 40&nbsp;€ ainsi que des frais supplémentaires de 1,3% par mois de retard.</p>
    </div>
    </div>
  </div>
</div>
</body>
</html>`

  return { html, nextNum, montantTotal }
}
